import { auth } from "../firebase/client";
import {
	type Attachment,
	AttachmentsApi,
	Configuration,
	type ContentTag,
	type CreateInboxItemRequest,
	type CreateProjectRequest,
	type Expense,
	ExpensesApi,
	type ExpenseType,
	ExpenseTypesApi,
	type InboxItem,
	IntegrationsApi,
	type KPI,
	type Message,
	MessagesApi,
	type Middleware,
	type Organization,
	OrganizationsApi,
	PaymentTypeNameEnum,
	type PlaceScoreResponse,
	type Project,
	type ProjectStatusEnum,
	ProjectsApi,
	TagsApi,
	type TelegramLinkResponse,
	type TelegramVerificationResponse,
	ToolsApi,
	type Unit,
	type UnitStatusEnum,
	UnitsApi,
	type UnitType,
	UnitTypesApi,
	type User,
	UsersApi,
	type VerifyTelegramLinkRequest,
} from "./openapi";

// Function to get the current Firebase auth token
async function getAccessToken() {
	if (!auth.currentUser) {
		throw new Error("User not authenticated");
	}

	try {
		return auth.currentUser?.getIdToken();
	} catch (error) {
		console.error("Error getting auth token:", error);
		throw new Error("Failed to get auth token");
	}
}

// Middleware to add authorization header to each request
const authMiddleware: Middleware = {
	pre: async (context) => {
		// If a token is already set in the headers, skip adding it again
		if (context.init.headers && context.init.headers["Authorization"]) {
			return context;
		}

		if (!context.init.headers) {
			context.init.headers = {};
		}

		try {
			const token = await getAccessToken();
			context.init.headers["Authorization"] = `Bearer ${token}`;
		} catch (error) {
			console.warn("Authentication token not available", error);
		}

		return context;
	},
};

// Configuration for API client
const config = new Configuration({
	basePath: process.env.NEXT_PUBLIC_BRIK_API_URL,
	middleware: [authMiddleware],
});

// Create and export service instances
const organizationsService = new OrganizationsApi(config);
const usersService = new UsersApi(config);
const projectsService = new ProjectsApi(config);
const unitsService = new UnitsApi(config);
const expensesService = new ExpensesApi(config);
const expenseTypesService = new ExpenseTypesApi(config);
const tagsService = new TagsApi(config);
const unitTypesService = new UnitTypesApi(config);
const messagesService = new MessagesApi(config);
const attachmentsService = new AttachmentsApi(config);
const toolsService = new ToolsApi(config);
const integrationService = new IntegrationsApi(config);
export {
	organizationsService,
	usersService,
	projectsService,
	unitsService,
	expensesService as expenseService,
	expenseTypesService,
	tagsService,
	unitTypesService,
	messagesService,
	attachmentsService,
	toolsService,
	integrationService,
	PaymentTypeNameEnum,
};

export type {
	Organization,
	CreateProjectRequest,
	UnitStatusEnum,
	Project,
	User,
	ContentTag,
	Unit,
	UnitType,
	Attachment,
	Expense,
	ExpenseType,
	ProjectStatusEnum,
	Message,
	TelegramLinkResponse,
	TelegramVerificationResponse,
	VerifyTelegramLinkRequest,
	PlaceScoreResponse,
	KPI,
	CreateInboxItemRequest,
	InboxItem,
};
